import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Text from "components/general/Text";
import ModalWrapper from "components/modals/ModalWrapper";
import AssigneeSelection from "components/inputs/AssigneeSelection";
import { useRecalculateMutation } from "apis/services/timekeeper";
import { RecalculateBody, RecalculateResponse } from "apis/types/timekeeper";
import usePermissions from "hooks/usePermission";
import showSuccessMsg from "utils/showSuccessMsg";
import getClassNames from "utils/getClassName";
import getBackendError from "utils/getBackendError";
import formatDate from "utils/formatDate";
import styles from "./styles.module.scss";

type RecalculateScope = "selected_staff" | "all";

interface AssigneeOption {
  label: string;
  value: number;
}

interface RecalculateProps {
  isVisible: boolean;
  setIsVisible: (state: boolean) => void;
}

const SCOPE_OPTIONS: {
  key: RecalculateScope;
  title: string;
  hint: string;
}[] = [
  {
    key: "selected_staff",
    title: "Selected employees",
    hint: "Recalculate the open shifts of the members you pick.",
  },
  {
    key: "all",
    title: "All accessible employees",
    hint: "Recalculate the open shifts of every member you have access to.",
  },
];

// The backend owns the range and resolves it in the business timezone, so this
// only mirrors its rule for display: the first day of the month two calendar
// months back, up to today. It is never sent with the request.
const getExpectedRange = () => ({
  from: moment().subtract(2, "months").startOf("month"),
  to: moment(),
});

const getSuccessMsg = (response?: RecalculateResponse) => {
  const {
    date_from,
    date_to,
    shifts_recalculated,
    shifts_resolved,
    shifts_remaining_open,
  } = response ?? {};
  if (shifts_recalculated === undefined)
    return "Open shifts have been recalculated successfully!";

  const range =
    date_from && date_to
      ? ` from ${formatDate(date_from)} to ${formatDate(date_to)}`
      : "";
  const outcome =
    shifts_resolved !== undefined && shifts_remaining_open !== undefined
      ? `: ${shifts_resolved} resolved, ${shifts_remaining_open} still open`
      : "";
  return `Recalculated ${shifts_recalculated} open shift${
    shifts_recalculated === 1 ? "" : "s"
  }${range}${outcome}.`;
};

export default function Recalculate({
  isVisible,
  setIsVisible,
}: RecalculateProps) {
  const [scope, setScope] = useState<RecalculateScope>("selected_staff");
  const [employees, setEmployees] = useState<AssigneeOption[]>([]);
  const [isAllAcknowledged, setIsAllAcknowledged] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [recalculate, { isLoading, error, reset }] = useRecalculateMutation();

  const { hasPermissionTo } = usePermissions();
  const canRecalculateAll = hasPermissionTo("recalculate_all_staff");
  const scopeOptions = SCOPE_OPTIONS.filter(
    (option) => option.key !== "all" || canRecalculateAll
  );

  // `reset` is rebuilt the moment a mutation starts, so it is held in a ref.
  // As an effect dependency it re-ran the effect below on submit, which threw
  // the form back to the default scope mid-request and wiped the mutation
  // state with it - taking the loading flag and any backend error along.
  const resetRef = useRef(reset);
  useEffect(() => {
    resetRef.current = reset;
  }, [reset]);

  // Reopening the modal starts clean, never from the leftovers of the previous
  // run - least of all a leftover "all" scope.
  useEffect(() => {
    if (!isVisible) return;
    setScope("selected_staff");
    setEmployees([]);
    setIsAllAcknowledged(false);
    setIsSubmitted(false);
    resetRef.current();
  }, [isVisible]);

  // Without the org-wide permission the scope can only be the selected staff.
  const isSelectedScope = scope === "selected_staff" || !canRecalculateAll;
  const missingEmployees = isSelectedScope && !employees.length;
  const isInvalid = isSelectedScope ? missingEmployees : !isAllAcknowledged;

  const employeesSummary = employees.length
    ? employees.map((item) => item.label).join(", ")
    : "No members selected";

  const expectedRange = getExpectedRange();

  const summaryRows = [
    ...(isSelectedScope
      ? [
          { title: "Scope", value: "Selected employees" },
          { title: `Members (${employees.length})`, value: employeesSummary },
        ]
      : [
          { title: "Scope", value: "All accessible employees" },
          { title: "Members", value: "Every member you have access to" },
        ]),
    { title: "Date From", value: formatDate(expectedRange.from.toDate()) },
    {
      title: "Date To",
      value: `${formatDate(expectedRange.to.toDate())} (today)`,
    },
  ];

  const onConfirm = () => {
    setIsSubmitted(true);
    // Guarding on isLoading keeps a double click from firing two requests.
    if (isInvalid || isLoading) return;

    const body: RecalculateBody = isSelectedScope
      ? {
          scope: "selected_staff",
          staff_ids: employees.map((item) => item.value),
        }
      : { scope: "all" };

    resetRef.current();
    recalculate(body)
      .unwrap()
      .then((response) => {
        showSuccessMsg({ msg: getSuccessMsg(response) });
        setIsVisible(false);
      })
      // The error middleware toasts it, the inline message below keeps it up.
      .catch(() => {});
  };

  const errorMessage = getBackendError(error);

  return (
    <ModalWrapper
      headerTitle="Recalculate open shifts"
      size="large"
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onConfirm={onConfirm}
      isLoading={isLoading}
      isBtnDisabled={isLoading}
    >
      <div className={styles.container}>
        <div>
          <Text className={styles.sectionHeader}>Recalculation scope</Text>
          <div className={styles.scopeOptions}>
            {scopeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                disabled={isLoading}
                aria-pressed={scope === option.key}
                className={getClassNames([
                  styles.scopeOption,
                  scope === option.key ? styles.selected : "",
                ])}
                onClick={() => setScope(option.key)}
              >
                <Text className={styles.scopeTitle}>{option.title}</Text>
                <Text className={styles.scopeHint}>{option.hint}</Text>
              </button>
            ))}
          </div>

          {isSelectedScope && (
            <div className={styles.fieldsGrid}>
              <div
                className={getClassNames([styles.field, styles.fullWidthField])}
              >
                <Text className={styles.inputLabel}>Members</Text>
                <AssigneeSelection
                  value={employees}
                  onChange={(value: AssigneeOption[]) =>
                    setEmployees(value ?? [])
                  }
                />
                {isSubmitted && missingEmployees && (
                  <Text className={styles.errorText}>
                    Select at least one member.
                  </Text>
                )}
              </div>
            </div>
          )}
        </div>

        {!isSelectedScope && (
          <div className={styles.warningBox}>
            <Text className={styles.warningTitle}>
              This recalculates every member you have access to
            </Text>
            <Text className={styles.warningText}>
              Every open shift in the date range below, for every member you
              have access to, is re-resolved. This can move check-in and
              check-out times that are already in payroll reports, and it cannot
              be undone from here.
            </Text>
            <label className={styles.warningConfirm} htmlFor="recalculate-all">
              <input
                id="recalculate-all"
                type="checkbox"
                checked={isAllAcknowledged}
                disabled={isLoading}
                onChange={(e) => setIsAllAcknowledged(e.target.checked)}
              />
              I understand and want to recalculate all of these open shifts.
            </label>
            {isSubmitted && !isAllAcknowledged && (
              <Text className={styles.errorText}>
                Confirm the warning before recalculating all open shifts.
              </Text>
            )}
          </div>
        )}

        <div className={styles.summarySection}>
          <Text className={styles.sectionHeader}>You are about to</Text>
          {summaryRows.map((row) => (
            <div key={row.title} className={styles.summaryRow}>
              <Text className={styles.summaryTitle}>{row.title}</Text>
              <Text className={styles.summaryValue}>{row.value}</Text>
            </div>
          ))}
          <Text className={styles.noticeText}>
            The date range is set automatically: from the first day of the month
            two months back, up to today. Only open shifts inside it are
            recalculated - older open shifts can still be recalculated one by
            one.
          </Text>
        </div>

        {!!errorMessage && (
          <Text className={styles.errorText}>{errorMessage}</Text>
        )}
      </div>
    </ModalWrapper>
  );
}
